/**
 * The walkthroughs she can give, written from the product rather than for it.
 *
 * Every sentence here comes from somewhere the platform already says it: a
 * suite's own summary and each screen's own one-liner (`layout/suites.ts`),
 * the guide's account of how a screen works (`guide.ts` MODULES), or the words
 * on the screen itself. That is what lets her walk a screen nobody wrote a
 * tour for — she reads its headings, tabs and search boxes off the page and
 * points at each — and it is also the limit she keeps: a section she has no
 * account of is named, not explained. Making up what a panel means is the one
 * thing a guide must not do.
 *
 * Tours follow the person's own menu (their suite order, their labels, what
 * they have hidden) and their access level, so she never walks someone onto a
 * screen they cannot open.
 */
import { t } from "../../i18n";
import { canSee, type AccessLevel } from "../access";
import { MODULES } from "../guide";
import { getScreen } from "../screen";
import { labelOf, SCREEN_BY_ROUTE, type NavLayout } from "../../layout/navigation";
import { SUITES, whatOf } from "../../layout/suites";
import { findByText, isVisible, nameOf, norm, type Target } from "./anchors";
import type { Step } from "./director";

const moduleFor = (route: string) => MODULES.find((m) => m.route === route);

/* a screen is pointed at by its title; the whole screen when it has none */
const TITLE: Target = { sel: "[data-sky='main'] h1" };
const MAIN: Target = { sel: "[data-sky='main']" };

/** The screens this person has in their menu and may open, suite by suite. */
function visibleGroups(layout: NavLayout, level: AccessLevel) {
  return layout.groups
    .map((g) => ({
      ...g,
      screens: g.screens.filter((to) => SCREEN_BY_ROUTE.has(to) && !layout.hidden.includes(to) && canSee(level, to)),
    }))
    .filter((g) => g.screens.length > 0);
}

const suiteLine = (id: string) => SUITES.find((x) => x.id === id)?.tagline ?? "";

function screenStep(layout: NavLayout, to: string, deep: boolean): Step {
  const label = labelOf(layout, to);
  const what = whatOf(to);
  const m = moduleFor(to);
  const parts = [`${label}. ${what || m?.what || ""}`.trim()];
  if (deep && m?.look) parts.push(m.look);
  return { title: label, route: to, target: TITLE, fallback: MAIN, text: parts.join(" ") };
}

/**
 * The whole platform. `full` walks every screen; otherwise one stop per suite,
 * on its first screen, which is the tour a new person actually sits through.
 */
export function siteTour(layout: NavLayout, level: AccessLevel, full: boolean): Step[] {
  const groups = visibleGroups(layout, level);
  const screens = groups.reduce((n, g) => n + g.screens.length, 0);
  const steps: Step[] = [
    {
      title: "Welcome",
      target: { sel: "[data-sky='sidebar']" },
      text:
        `This is the menu: ${groups.length} suites and ${screens} screens you can open, arranged the way you have them. ` +
        "I will walk you through it — press Next to move on, or Escape to stop at any point.",
    },
    {
      title: "Site",
      target: { sel: "[data-sky='site']" },
      text: "Everything you see is for the site chosen here, by its ICAO code. Change it and every screen follows.",
    },
  ];
  for (const g of groups) {
    const head = g.name;
    const line = suiteLine(g.id);
    steps.push({
      title: head,
      target: { sel: `[data-sky-suite="${g.id}"]` },
      text: `${head}. ${line}`.trim() + (full ? "" : ` ${g.screens.length === 1 ? "One screen" : `${g.screens.length} screens`} in it.`),
    });
    for (const to of full ? g.screens : g.screens.slice(0, 1)) steps.push(screenStep(layout, to, full));
  }
  steps.push(
    {
      title: "Search and tools",
      target: { sel: "[data-sky='tools']" },
      text: "Search, the daylight view and the work register's export and import sit up here, on every screen.",
    },
    {
      title: "Language",
      target: { sel: "[data-sky='language']" },
      text: "Switch between English and Arabic here; the whole platform turns with it.",
    },
    {
      title: "Customize the menu",
      target: { sel: "[data-sky='customize']" },
      text: "Rename, reorder or hide screens and suites from here. Tours follow the menu as you arrange it.",
    },
    {
      title: "Sky",
      target: { sel: "[data-sky='sky']" },
      text:
        "And this is me. Ask me what is urgent, to review the critical items across the system, to show you around any screen, or where something is — I will take you there and point at it.",
    },
  );
  return steps;
}

/** One suite, every screen in it. */
export function suiteTour(layout: NavLayout, level: AccessLevel, suiteId: string): Step[] {
  const g = visibleGroups(layout, level).find((x) => x.id === suiteId);
  if (!g) return [];
  const head = g.name;
  const line = suiteLine(g.id);
  return [
    { title: head, target: { sel: `[data-sky-suite="${g.id}"]` }, text: `${head}. ${line}`.trim() },
    ...g.screens.map((to) => screenStep(layout, to, true)),
  ];
}

/** The suite a question names, by its id or its name as this person has it. */
export function findSuite(layout: NavLayout, q: string): string | null {
  const text = ` ${norm(q)} `;
  let best: { id: string; len: number } | null = null;
  for (const g of layout.groups) {
    for (const key of [norm(g.name), norm(t(g.name)), g.id.replace(/-/g, " ")]) {
      if (key.length > 3 && text.includes(` ${key} `) && (!best || key.length > best.len)) best = { id: g.id, len: key.length };
    }
  }
  return best?.id ?? null;
}

/** A screen a question names, by its label in this person's menu or the guide's name for it. */
export function findScreen(layout: NavLayout, level: AccessLevel, q: string): string | null {
  const text = ` ${norm(q)} `;
  let best: { to: string; len: number } | null = null;
  for (const g of layout.groups) {
    for (const to of g.screens) {
      if (!canSee(level, to) || !SCREEN_BY_ROUTE.has(to)) continue;
      const base = [norm(labelOf(layout, to)), norm(t(labelOf(layout, to))), norm(moduleFor(to)?.name ?? "")];
      /* "raise a work order" names Work orders: the singular counts too */
      const keys = [...base, ...base.filter((k) => k.endsWith("s")).map((k) => k.slice(0, -1))];
      for (const key of keys) {
        if (key.length > 3 && text.includes(` ${key} `) && (!best || key.length > best.len)) best = { to, len: key.length };
      }
    }
  }
  return best?.to ?? null;
}

/**
 * Every screen this person may open, by route and name — what the model is
 * told it can offer and open. The same test `findScreen` applies, so a
 * screen above the person's level is never named to them by the model
 * either.
 */
export function screenList(layout: NavLayout, level: AccessLevel): { route: string; title: string }[] {
  const out: { route: string; title: string }[] = [];
  const seen = new Set<string>();
  for (const g of layout.groups) {
    for (const to of g.screens) {
      if (seen.has(to) || !canSee(level, to) || !SCREEN_BY_ROUTE.has(to)) continue;
      seen.add(to);
      const what = moduleFor(to)?.name;
      const label = labelOf(layout, to);
      out.push({ route: to, title: what && what !== label ? `${label} (${what})` : label });
    }
  }
  return out;
}

/* ── a screen, read off the page ──────────────────────────────────────── */

interface Landmark {
  target: Target;
  name: string;
  kind: "heading" | "tabs" | "search" | "table" | "summary";
  sub?: string;
}

function subOf(h: HTMLElement): string {
  const sib = h.nextElementSibling;
  if (sib instanceof HTMLElement && !/^H\d$/.test(sib.tagName) && isVisible(sib)) {
    const txt = (sib.innerText || "").trim().replace(/\s+/g, " ");
    if (txt.length > 8 && txt.length < 220) return txt;
  }
  return "";
}

/**
 * The parts of the screen in front of the person, in reading order: its
 * sections by their headings, its tab strips, its search and filter boxes.
 * Capped, because a tour of forty panels is not a tour.
 */
export function landmarks(max = 8): Landmark[] {
  const main = document.querySelector("[data-sky='main']") ?? document.querySelector("main");
  if (!main) return [];
  const out: Landmark[] = [];
  const seen = new Set<string>();
  const nodes = Array.from(
    main.querySelectorAll<HTMLElement>(
      "h1, h2, h3, [role='tablist'], input[type='search'], input[placeholder], table, [data-sky-landmark]",
    ),
  );
  for (const el of nodes) {
    if (out.length >= max) break;
    if (!isVisible(el) || el.closest("[data-sky-stage], [data-sky-dock]")) continue;
    let lm: Landmark | null = null;
    if (el.matches("h1, h2, h3")) {
      const name = nameOf(el);
      if (name.length < 3 || name.length > 90) continue;
      lm = { target: { text: name, kind: "heading" }, name, kind: "heading", sub: subOf(el) };
    } else if (el.matches("[role='tablist']")) {
      const tabs = Array.from(el.querySelectorAll<HTMLElement>("[role='tab']")).map(nameOf).filter(Boolean);
      if (tabs.length < 2) continue;
      const first = tabs[0];
      lm = { target: { text: first, kind: "control" }, name: tabs.slice(0, 6).join(", "), kind: "tabs" };
    } else if (el.matches("input")) {
      const name = nameOf(el);
      if (!/search|filter|find/i.test(`${name} ${(el as HTMLInputElement).placeholder}`)) continue;
      lm = { target: { text: name, kind: "field" }, name, kind: "search" };
    } else if (el.matches("table")) {
      const heads = Array.from(el.querySelectorAll("th")).map((th) => (th.textContent ?? "").trim()).filter(Boolean);
      if (heads.length < 2) continue;
      const cap = el.querySelector("caption")?.textContent?.trim();
      lm = { target: { sel: "[data-sky-lit-table]" }, name: cap || heads.slice(0, 5).join(", "), kind: "table" };
      /* a table has no name to find it by, so it is marked for the length of the tour */
      el.setAttribute("data-sky-lit-table", String(out.length));
      lm.target = { sel: `[data-sky-lit-table="${out.length}"]` };
    }
    if (!lm) continue;
    const key = `${lm.kind}|${norm(lm.name)}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(lm);
  }
  return out;
}

function landmarkStep(lm: Landmark, title: string): Step {
  switch (lm.kind) {
    case "heading":
      return { title, target: lm.target, text: lm.sub ? `${lm.name} — ${lm.sub}` : `This section is ${lm.name}.` };
    case "tabs":
      return { title, target: lm.target, text: `These tabs switch the view: ${lm.name}.` };
    case "search":
      return { title, target: lm.target, text: `${lm.name}: type here to narrow what the screen shows.` };
    case "table":
      return { title, target: lm.target, text: `The register itself, with columns for ${lm.name}.` };
    default:
      return { title, target: lm.target, text: lm.name };
  }
}

/**
 * The screen the person is on. The screen's own summary first, when it has
 * published one; the guide's account of how it works; then its parts, read off
 * the page. The desk's summary view is opened out first, since the parts are
 * underneath it.
 */
export function screenTour(layout: NavLayout, route: string): Step[] {
  const path = route.split("?")[0];
  const label = SCREEN_BY_ROUTE.has(path) ? labelOf(layout, path) : getScreen()?.title ?? "This screen";
  const steps: Step[] = [];
  const scr = getScreen();
  const m = moduleFor(path);
  const what = whatOf(path);
  const first = [what || m?.what, m?.how].filter(Boolean).join(" ");
  steps.push({ title: label, target: TITLE, fallback: MAIN, text: first || `This is ${label}.` });
  if (scr && scr.route === path && scr.summary.length > 0) {
    steps.push({ title: label, target: TITLE, fallback: MAIN, text: `What it says right now: ${scr.summary.slice(0, 2).join(" ")}` });
  }
  const full = findByText("Open the full desk", "control");
  if (full) steps.push({ title: label, target: { text: "Open the full desk", kind: "control" }, action: "press", text: "The full desk is under this summary — opening it." });
  steps[steps.length - 1].grow = () => partsTour(label, path);
  return steps;
}

/** The parts of the page, as steps. Run after the screen is open, since that is when they exist. */
export function partsTour(title: string, route: string): Step[] {
  const steps = landmarks().map((lm) => landmarkStep(lm, title));
  const m = moduleFor(route);
  if (m?.look) steps.push({ title, text: `Worth knowing: ${m.look}` });
  if (steps.length === 0) steps.push({ title, text: "That is the screen. Ask me about anything on it and I will point to it." });
  return steps;
}

/** One screen, from wherever the person is: through the menu, then its account. */
export function screenVisit(layout: NavLayout, to: string): Step[] {
  const label = labelOf(layout, to);
  const m = moduleFor(to);
  const what = whatOf(to);
  const steps: Step[] = [
    { title: label, route: to, target: TITLE, fallback: MAIN, text: `${label}. ${what || m?.what || ""}`.trim() },
  ];
  if (m?.how) steps.push({ title: label, target: TITLE, fallback: MAIN, text: m.how });
  const last = steps[steps.length - 1];
  last.grow = () => {
    const open = findByText("Open the full desk", "control");
    const lead: Step[] = open
      ? [{ title: label, target: { text: "Open the full desk", kind: "control" }, action: "press", text: "The full desk is under this summary — opening it." }]
      : [];
    if (lead.length) lead[0].grow = () => partsTour(label, to);
    return lead.length ? lead : partsTour(label, to);
  };
  return steps;
}

/* ── "where is …" ─────────────────────────────────────────────────────── */

/**
 * The thing a "where is" question is after, or null.
 *
 * Narrow on purpose. "Show me the overdue work orders" is a question about the
 * records and belongs to the platform's catalogue; only "where is …", "point
 * to …", "highlight …", or "show me the … button / field / tab" is a question
 * about the screen.
 */
export function whereIsTarget(q: string): string | null {
  const t = q.trim().replace(/^sky,?\s+/i, "").replace(/[?.!]+$/, "");
  const ui = "button|field|box|link|tab|menu|section|panel|filter|search box|search bar|toggle|switch|control|icon";
  let m = t.match(/^(?:where(?:'s| is| are| do i find| can i find| would i find)|point (?:me )?(?:to|at)|highlight)\s+(?:the\s+|a\s+|an\s+|my\s+)?(.+)$/i);
  if (!m) m = t.match(new RegExp(`^show me (?:the\\s+|a\\s+|an\\s+)?(.+?\\s(?:${ui}))$`, "i"));
  if (!m) return null;
  const thing = m[1]
    .replace(/\s+(?:on|in) (?:this|the) (?:screen|page|desk)$/i, "")
    .replace(new RegExp(`\\s+(?:${ui})$`, "i"), "")
    .trim();
  if (!thing || thing.length < 3 || /^(around|how|me around|what|i )/i.test(thing)) return null;
  return thing;
}
